export const formatCurrency = (amount: number): string => {
  // Handle NaN, undefined, null, or non-finite values
  if (!amount || !isFinite(amount)) {
    return "$0.00";
  }
  
  // Round to 2 decimal places to avoid floating point precision issues
  const roundedAmount = Math.round(amount * 100) / 100;
  
  // Handle negative zero or amounts that round to zero
  if (roundedAmount === 0 || Object.is(roundedAmount, -0)) {
    return "$0.00";
  }
  
  const formattedAmount: string = Math.abs(roundedAmount).toLocaleString("es-MX", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return roundedAmount < 0 ? `-$${formattedAmount}` : `$${formattedAmount}`;
};

export const formatNumber = (number: number): string => {
  if (number) {
    return Math.abs(number).toLocaleString("es-MX");
  }
  return "0";
};

/**
 * Formats a decimal number with appropriate decimal places (2-4 decimals as needed)
 * Uses 2 decimals if value has 2 or less, otherwise uses up to 4 decimals (trimming trailing zeros)
 */
export const formatDecimal = (value: number): string => {
  const fixed2 = value.toFixed(2);
  const fixed4 = value.toFixed(4);

  // If the 4-decimal version ends with "00", use 2 decimals
  if (fixed4.endsWith("00")) {
    return fixed2;
  }

  // Remove trailing zeros but keep minimum 2 decimal places
  const trimmed = fixed4.replace(/0+$/, "");
  const decimalPlaces = trimmed.split(".")[1]?.length || 0;

  if (decimalPlaces < 2) {
    return fixed2;
  }

  return trimmed;
};

/**
 * Formats a percentage with appropriate decimal places (2-4 decimals as needed)
 * Uses 2 decimals if value has 2 or less, otherwise uses up to 4 decimals (trimming trailing zeros)
 */
export const formatPercentage = (value: number): string => {
  return `${formatDecimal(value)}%`;
};

/**
 * Amount that the response may not carry. An absent value renders an em dash, because
 * `formatCurrency` prints anything falsy as "$0.00" and a figure that was never computed is not zero.
 */
export const formatOptionalCurrency = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined || !isFinite(amount)) {
    return "—";
  }
  return formatCurrency(amount);
};

/**
 * Quantity in pieces. An absent value renders an em dash: it means the figure was not computed,
 * which is not the same as a zero.
 */
export const formatQuantity = (quantity: number | null | undefined): string => {
  if (quantity === null || quantity === undefined || !isFinite(quantity)) {
    return "—";
  }
  return formatDecimal(quantity);
};

/** Quantity in pieces keeping its sign, so a positive difference reads as a surplus. */
export const formatSignedQuantity = (quantity: number | null | undefined): string => {
  const formatted: string = formatQuantity(quantity);
  return Number(quantity) > 0 ? `+${formatted}` : formatted;
};

export const formatRatePercentage = (percentage: number | null | undefined): string =>
  percentage === null || percentage === undefined ? "—" : formatPercentage(percentage);
